
import { defineComponent, reactive, toRefs, ref, onMounted, onActivated } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUserInfoStore } from "@/stores/UserInfoStore";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import _ from "lodash";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import IResManufMethodDTO from "@/models/dto/IResManufMethodDTO";
import IResKaizenItemDTO from "@/models/dto/IResKaizenItemDTO";
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import ElTable from "element-plus/lib/components/table";
/** Utils */
import { delay } from "@/plugins/utility";
import IReqKaizenItemSearchCriteriaDTO from "@/models/dto/IReqKaizenItemSearchCriteriaDTO";
import { DataTableType } from "@/models/enums/DataTableType";
import KaizenItemViewModel from "@/models/KaizenItemViewModel";
import IReqKaizenItemDTO from "@/models/dto/IReqKaizenItemDTO";

export default defineComponent({
  props: {
    pjcode: String,
  },
  setup(props) {
    const { t } = useI18n();
    const userInfoStore = useUserInfoStore();
    const paginatorSetup = usePaginatorSetup();
    const tableType = DataTableType.kaizenItemTable;

    const state = reactive({
      isTableLoading: false,
      dialogTopVh: "1vh",
      formLabelPosition: "right" as "top" | "left" | "right",
      formItemSize: "default" as "default" | "small",
      formLabelWidth: "180px" as string,

      // 工藝類別選項
      allManufMethodList: [] as IResManufMethodDTO[],
      isManufMethodListLoading: false,

      // 改善事項清單
      displayKaizenList: [] as IResKaizenItemDTO[],

      //搜尋條件
      searchCriteria: {
        page: 1,
        pageSize: 20,
        manufMethodIds: [],
      } as IReqKaizenItemSearchCriteriaDTO,

      //匯出用的搜尋條件
      exportCriteria: {} as IReqKaizenItemSearchCriteriaDTO,

      //編輯彈窗
      isShowModifyDialog: false,
      isModifyDialogLoading: false,
      selectedKaizenItem: {} as IResKaizenItemDTO,

      //瀏覽彈窗
      isShowViewDialog: false,

      filteredTotal: 0,
      colDisplayCtrl: new Array(50).fill(true) as boolean[],
    });

    //#region 建立表單ref與Validator
    const kaizenItemTableRef = ref<InstanceType<typeof ElTable>>();

    const modifyKaizenItemFormRef = ref();
    const modifyKaizenItemFormRules = ref({
      impPlan: [{ required: true, message: () => t("form.validation_msg.improvement_plan_is_required"), trigger: "blur" }],
      impMethod: [{ required: true, message: () => t("form.validation_msg.improvement_method_is_required"), trigger: "blur" }],
      impMethodDetail: [{ required: true, message: () => t("form.validation_msg.implement_detail_is_required"), trigger: "blur" }],
      implementDay: [{ required: true, message: () => t("form.validation_msg.implement_day_is_required"), trigger: "blur" }],
    });

    const teamNumSearchValidator = async (source: any, value: any, callback: any) => {
      const filed = source.field as "teamNum";
      // console.log(`validate(uint) : ${source.field}`);
      if (!value) {
        state.searchCriteria[filed] = undefined;
        return;
      }

      var inputNum = Number(value);
      if (Number.isNaN(inputNum)) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      if (inputNum <= 0) {
        callback(new Error(t("validation_msg.invalid_uint_value")));
        return;
      }

      state.searchCriteria[filed] = Math.round(inputNum);
      callback();
    };
    const searchCriteriaFormRef = ref();
    const searchCriteriaFormRules = ref({
      teamNum: [{ validator: teamNumSearchValidator, trigger: "blur" }],
    });
    //#endregion

    //#region Hook functions
    onMounted(async () => {
      //console.log(`KaizenList v2 onMounted :  props.pjcode=${props.pjcode}`);
      state.searchCriteria.pjcode = props.pjcode;

      //更新使用者設置偏好
      await updateUserPreference();

      //工藝類別選項
      await getAllManufMethods();

      //取得改善事項清單
      await searchKaizenItem();
    });
    onActivated(async () => {
      //console.log(`KaizenList v2 onActivated : props.pjcode=${props.pjcode}`);
      state.searchCriteria.pjcode = props.pjcode;

      //更新使用者設置偏好
      await updateUserPreference();

      //工藝類別選項
      await getAllManufMethods();

      //取得改善事項清單
      await searchKaizenItem();
    });
    //#endregion

    //#region UI Events
    /** 按下清除按鈕 */
    const onClearSearchKaizenClicked = () => {
      state.searchCriteria.pjcode = undefined;
      state.searchCriteria.creator = undefined;
      state.searchCriteria.teamNum = undefined;
      state.searchCriteria.pn = undefined;
      state.searchCriteria.startDate = undefined;
      state.searchCriteria.endDate = undefined;
      state.searchCriteria.manufMethodIds.length = 0;

      //清除table排序
      kaizenItemTableRef.value?.clearSort();
    };

    /** 顯示工藝類別下拉選單時 */
    const onManufMethodListShown = async () => {
      await getAllManufMethods();
    };

    /** 按下搜尋按鈕 */
    const onSearchKaizenClicked = async () => {
      try {
        //清除table排序
        kaizenItemTableRef.value?.clearSort();

        /* 取得改善事項清單 */
        await searchKaizenItem();
      } catch (error) {
        console.error(error);
      }
    };

    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      //console.log("onInputFieldEnterKeyUp");

      /* 取得改善事項清單 */
      await searchKaizenItem();
    };

    /** 改變排序 */
    const onSortChanged = (sort: any) => {
      console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      state.searchCriteria.sortColName = sort.prop;
      state.searchCriteria.sortOrder = sort.order;
      searchKaizenItem();
      console.log("onCurrentPageChanged > scroll to 0,0");
      window.scrollTo(0, 0);
    };

    /** 分頁輸入 */
    const onPagesizeChanged = async () => {
      /* 取得改善事項清單 */
      await searchKaizenItem();
      window.scrollTo(0, 0);
    };

    const onCurrentPageChanged = async () => {
      /* 取得改善事項清單 */
      await searchKaizenItem();
      window.scrollTo(0, 0);
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };

    /** 按下編輯按鈕 */
    const onEditKaizenItemClicked = async (target: IResKaizenItemDTO) => {
      try {
        state.isTableLoading = true;

        await GetSelectedItemDetailAsync(target.id);

        setFormStyle();

        state.isShowModifyDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 按下瀏覽按鈕 */
    const onViewKaizenItemClicked = async (target: IResKaizenItemDTO) => {
      try {
        state.isTableLoading = true;

        await GetSelectedItemDetailAsync(target.id);

        setFormStyle();
        
        state.isShowViewDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };
    const onModifyDialogClosing = () => {
      modifyKaizenItemFormRef.value?.clearValidate();
    };

    /** 按下修改存檔按鈕 */
    const onSaveModifyResultClicked = async () => {
      try {
        await modifyKaizenItemFormRef?.value.validate();

        await ElMessageBox.confirm(t("Modify_Confirm_Msg"), t("Warning"), {
          confirmButtonText: t("Confirm"),
          cancelButtonText: t("Cancel"),
          type: "warning",
        });
      } catch (error) {
        console.log(error);
        return;
      }

      try {
        state.isModifyDialogLoading = true;
        const patchedKaizenItem: IReqKaizenItemDTO = {
          impPlan: state.selectedKaizenItem.impPlan,
          impMethod: state.selectedKaizenItem.impMethod,
          impMethodDetail: state.selectedKaizenItem.impMethodDetail,
          implementDay: state.selectedKaizenItem.implementDay,
          hyperlink: state.selectedKaizenItem.hyperlink,

          //下方的項目暫且不允許被修改(保留往後會有修改需求)
          projectCode: state.selectedKaizenItem.projectCode,
          pn: state.selectedKaizenItem.pn,
          workNum: Number(state.selectedKaizenItem.workNum),
          manufMethodId: state.selectedKaizenItem.manufMethodId,
          procTimeAfterImp: Number(state.selectedKaizenItem.procTimeAfterImp),
          customerName: "",
        };
        const modifySiteResponse = await API.patchKaizenItem(state.selectedKaizenItem.id, patchedKaizenItem);
        state.isModifyDialogLoading = false;

        if (modifySiteResponse.data.result !== APIResultCode.success) {
          throw new Error(modifySiteResponse.data.msg);
        }

        state.isShowModifyDialog = false;
        ElMessage({
          showClose: true,
          message: t("modify_success"),
          type: "success",
        });

        /* 刷新資料 */
        state.isTableLoading = true;
        await searchKaizenItem();
        state.isTableLoading = false;
      } catch (error) {
        console.error(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isModifyDialogLoading = false;
        state.isTableLoading = false;
      }
    };

    /** 按下超連結 */
    const onHyperlinkClicked = async (url: string) => {
      window.open(url, "_blank");
    };

    /** 按下匯出按鈕 */
    const onExportReportClicked = async () => {
      try {
        state.isTableLoading = true;
        const exportResponse = await API.exportKaizenItemsBySearchCriteria(state.exportCriteria);
        if (exportResponse.data.result !== 1) {
          ElNotification({
            title: "錯誤",
            dangerouslyUseHTMLString: true,
            message: t("export_failed") + `((:<br>${exportResponse.data.msg}`,
            type: "error",
          });
          return;
        }

        const FileName = exportResponse.data.content;
        const DownloadReportResponse = await API.downloadReportFile(FileName);

        var blob = new Blob([DownloadReportResponse.data], { type: "application/" + DownloadReportResponse.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.setAttribute("style", "display: none");
        a.href = url;
        a.download = FileName;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove(); // remove the element
      } catch (error) {
        console.log(error);
      } finally {
        state.isTableLoading = false;
      }
    };
    //#endregion

    //#region Private Functions
    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 更新所選項目的詳細資料 */
    const GetSelectedItemDetailAsync = async (kaizenId: number) => {
      try {
        const getKaizenItemResponse = await API.getKaizenItemById(kaizenId);
        if (getKaizenItemResponse.data.result !== APIResultCode.success) {
          throw new Error(getKaizenItemResponse.data.msg);
        }

        state.selectedKaizenItem = getKaizenItemResponse.data.content;
      } catch (error) {
        console.log(error);
      }
    };

    /** 呼叫API, 取得所有工藝類別 */
    async function getAllManufMethods() {
      try {
        /* Table Loading Flag 開啟 */
        state.isManufMethodListLoading = true;

        /* 若 開發時 等待 1 秒 */
        if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);
        const getResult = await API.getManufMethods();

        if (getResult.data.result !== APIResultCode.success) {
          throw new Error(getResult.data.msg);
        }

        state.allManufMethodList = getResult.data.content;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        /* Table Loading Flag 關閉 */
        state.isManufMethodListLoading = false;
      }
    }

    /** 呼叫API, 取得所有改善事項清單 */
    async function searchKaizenItem() {
      try {
        /* 檢查欄位 */
        await searchCriteriaFormRef.value.validate();
      } catch (error) {
        return;
      }

      try {
        /* Table Loading Flag 開啟 */
        state.isTableLoading = true;

        copySearchCriteria();

        const getResult = await API.searchKaizenItem(state.searchCriteria);
        if (getResult.data.result !== APIResultCode.success) {
          throw new Error(getResult.data.msg);
        }

        //取得改善事項清單
        state.displayKaizenList = getResult.data.content.pageItems;

        //更新分頁資料總數
        state.filteredTotal = getResult.data.content.totalItems;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        state.isTableLoading = false;
      }
    }

    /** 備份搜尋條件(供匯出使用) */
    function copySearchCriteria() {
      state.exportCriteria.creator = state.searchCriteria.creator;
      state.exportCriteria.pjcode = state.searchCriteria.pjcode;
      state.exportCriteria.manufMethodIds = state.searchCriteria.manufMethodIds;
      state.exportCriteria.pn = state.searchCriteria.pn;
      state.exportCriteria.teamNum = state.searchCriteria.teamNum;
      state.exportCriteria.startDate = state.searchCriteria.startDate;
      state.exportCriteria.endDate = state.searchCriteria.endDate;
    }

    /** 檢驗輸入的字串是否為合法的URL */
    function validHyperlink(input: string): boolean {
      console.log(`valid hyperlink : ${input}`);
      try {
        new URL(input);
        return true;
      } catch (error) {
        return false;
      }
    }

    /** RWD設置 */
    function setFormStyle() {
      // small
      if (window.innerWidth <= 576) {
        state.formLabelPosition = "right";
        state.formItemSize = "default";
        state.formLabelWidth = "auto";
        state.dialogTopVh = "1vh";
        return;
      }

      // medium
      if (window.innerWidth <= 768) {
        state.formLabelPosition = "right";
        state.formItemSize = "default";
        state.formLabelWidth = "180px";
        state.dialogTopVh = "3vh";
        return;
      }

      state.formLabelPosition = "right";
      state.formItemSize = "default";
      state.formLabelWidth = "180px";
      state.dialogTopVh = "8vh";
    }
    //#endregion

    return {
      ...toRefs(state),
      paginatorSetup,
      userInfoStore,
      t,
      modifyKaizenItemFormRef,
      modifyKaizenItemFormRules,
      searchCriteriaFormRef,
      searchCriteriaFormRules,

      // function
      onClearSearchKaizenClicked,
      onSearchKaizenClicked,
      onPagesizeChanged,
      onCurrentPageChanged,
      onSortChanged,
      onManufMethodListShown,
      onInputFieldEnterKeyUp,
      onColDisplayCtrlPanelHidded,
      onEditKaizenItemClicked,
      onViewKaizenItemClicked,
      onSaveModifyResultClicked,
      onModifyDialogClosing,
      onHyperlinkClicked,
      onExportReportClicked,
      validHyperlink,
    };
  },
});
